import usePrefersReducedMotion from 'hooks/usePrefersReducedMotion'
import { buildTranslate } from 'locales'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { TourPager, TourPagerLabels } from './TourPager'
import type { DOMRectLike } from './TourSpotlight'
import { TourSpotlight } from './TourSpotlight'
import { TOUR_STEPS } from './tourSteps'
import { TourTooltipCard } from './TourTooltipCard'
import type { TourStep } from './types'
import { useWizardTour } from './useWizardTour'

interface WizardTourProps {
  // From auth.current_merchant_id at the mount site (Testing.tsx). 0/null means
  // "no merchant yet" — the tour stays dormant.
  merchantId: number
  // The ordered steps (defaults to the first tour). Testing.tsx passes TOUR2_STEPS
  // for the second tour.
  steps?: TourStep[]
  // i18n namespace for this tour's copy (steps.* / pager.* / dialog_label).
  translateKey?: string
  // Whether the tour may auto-start. The HOST owns the show policy (AI-255):
  // it gates on onboarding_completed (finished merchants never see it) AND a
  // provider-scoped per-visit "already shown" flag, so a returning merchant who
  // hasn't finished setup sees the tour again. When false the tour stays dormant.
  enabled?: boolean
  // Fired once, when the tour actually OPENS. `enabled` alone cannot tell a host
  // "about to open" from "will never open" — the anchor wait below has no
  // deadline of its own — so a host that sequences other UI on this tour needs
  // this to disarm its own never-started watchdog (AI-921).
  onStart?: () => void
  // Fired once when the tour finishes (final CTA) or is dismissed (Esc), so the
  // host can record the per-visit "shown" flag. Persistence is the host's job —
  // the tour keeps no localStorage of its own.
  onSeen?: () => void
  // Stable element the auto-start / re-measure MutationObservers watch instead of
  // document.body. The host passes the (non-remounting) wrapper around the
  // simulator so the observers wake only on the simulator's own DOM churn — the
  // persona-switch remount and the auto-playing transcript both happen inside it
  // — not on every mutation anywhere in the app. Falls back to document.body
  // when omitted (e.g. test hosts that append anchors straight to the body).
  observeRef?: React.RefObject<HTMLElement>
}

const toRectLike = (rect: DOMRect): DOMRectLike => ({
  top: rect.top,
  left: rect.left,
  width: rect.width,
  height: rect.height,
})

const rectsEqual = (a: DOMRectLike | null, b: DOMRectLike | null): boolean => {
  if (a === b) return true
  if (!a || !b) return false
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  )
}

// Arrow keys page the tour, but not while the merchant is moving a text cursor
// in a spotlit control (e.g. tour 2's chat-input step, reachable through the
// pointer-transparent scrim) — that would advance the tour out from under them.
const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  )
}

// The assembled AI-162 tour overlay: drives the pure useWizardTour state machine,
// measures the active F7 anchor each step, spotlights it, shows the tooltip card
// + pager, and reports back (onSeen) when it finishes/dismisses so the host can
// record its per-visit flag. Mounted ONLY on the wizard Testing stage. NOT a
// React.FC.
const WizardTour = ({
  merchantId,
  steps = TOUR_STEPS,
  translateKey = 'recovery_ai.wizard.tour',
  enabled = true,
  onStart,
  onSeen,
  observeRef,
}: WizardTourProps): JSX.Element | null => {
  const t = buildTranslate(translateKey)
  const stepIds = steps.map(step => step.id)

  const {
    isActive,
    currentIndex,
    currentStep,
    start,
    next,
    back,
    goTo,
    skip,
    finish,
  } = useWizardTour({
    steps,
    onFinish: onSeen,
    onSkip: onSeen,
  })

  const [targetRect, setTargetRect] = useState<DOMRectLike | null>(null)

  // Mirrored into a ref so an inline arrow at the call site cannot re-run the
  // auto-start effect below every render (it would reconnect its
  // MutationObserver each time; the onFinishRef pattern, useWizardTour.ts:47-56).
  const onStartRef = useRef(onStart)
  useEffect(() => {
    onStartRef.current = onStart
  }, [onStart])

  // Auto-start once when the host has enabled the tour, a merchant id is present,
  // AND the simulator has actually rendered its controls (the first F7 anchor is
  // in the DOM). WizardSimulator renders a loading/error state before the engine,
  // so gating on the anchor avoids opening the tour over a still-loading simulator
  // — and avoids opening it at all in a host that renders no simulator (e.g. a
  // test mock without anchors), where a stray centered card would collide with the
  // host's own controls. The host's `enabled` carries the show policy
  // (onboarding_completed + per-visit dedup); startedRef makes it strictly
  // once-per-mount on top of that.
  const startedRef = useRef(false)
  useEffect(() => {
    if (!enabled) return undefined
    if (startedRef.current) return undefined
    if (!merchantId) return undefined

    const firstAnchorId = steps[0]?.anchorId
    const tryStart = (): boolean => {
      if (startedRef.current) return true
      if (
        firstAnchorId &&
        document.querySelector(`[data-tour="${firstAnchorId}"]`)
      ) {
        startedRef.current = true
        start()
        // After start() and behind every bail, so a dormant tour never reports.
        onStartRef.current?.()
        return true
      }
      return false
    }

    if (tryStart()) return undefined
    // Anchors mount asynchronously (after the simulator's config load / phase
    // change); wait for the first one to appear, then start once and stop
    // observing. Scoped to the simulator wrapper (the first anchor is inside it)
    // so we don't wake on unrelated app-wide mutations.
    const observeTarget = observeRef?.current ?? document.body
    const observer = new MutationObserver(() => {
      if (tryStart()) observer.disconnect()
    })
    observer.observe(observeTarget, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [enabled, merchantId, steps, start, observeRef])

  // If the host disables the tour WHILE it is running, tear it down. `isActive`
  // is otherwise owned entirely by useWizardTour and only clears via skip/finish,
  // so a mid-run `enabled` flip (e.g. the merchant finishes the guided phase —
  // which disables THIS tour and enables the second one) would leave two
  // coach-mark tours overlapping. skip() marks it seen, which is correct: the
  // merchant has moved past this tour's phase.
  useEffect(() => {
    if (isActive && !enabled) skip()
  }, [isActive, enabled, skip])

  // Measure the active step's anchor. Kept stable per step (deps on the step's
  // stable anchorId) so the resize/scroll listeners aren't re-bound every render.
  const anchorId = currentStep?.anchorId
  // Idempotent: bail out of the state update when neither the anchor element nor
  // its rect changed, so the DOM-observing re-measure below (which can fire on
  // unrelated mutations) never churns a re-render.
  const measure = useCallback(() => {
    if (!anchorId) {
      setTargetRect(null)
      return
    }
    const el = document.querySelector<HTMLElement>(`[data-tour="${anchorId}"]`)
    const rect = el ? toRectLike(el.getBoundingClientRect()) : null
    setTargetRect(prev => (rectsEqual(prev, rect) ? prev : rect))
  }, [anchorId])

  useEffect(() => {
    if (!isActive) return undefined
    measure()
    window.addEventListener('resize', measure)
    // capture:true so we also re-measure on scrolls of inner containers.
    //   context: coalesced through rAF, not called directly. A raw
    //   addEventListener callback re-renders synchronously and unbatched (true
    //   for every React version this repo has run), so a burst of native
    //   scroll events — e.g. the AI-1117 scrollIntoView below, animating
    //   toward AI-1123's now `position: sticky` playbackNav anchor, whose rect
    //   keeps changing for the whole animation instead of settling in one
    //   tick — chains straight into TourTooltipCard's own remeasurement
    //   effect without ever yielding to a paint, which is what trips React's
    //   "Maximum update depth exceeded" (#185). Collapsing to one measure()
    //   per animation frame forces a yield between updates.
    let scrollRaf: number | null = null
    const onScroll = () => {
      if (scrollRaf !== null) return
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = null
        measure()
      })
    }
    window.addEventListener('scroll', onScroll, true)
    // Re-measure when the anchor (re)appears or moves. Two things move it under
    // a running tour: switching persona remounts SimulatorEngine (its engineKey),
    // and AI-921's transcript reveals a message at a time — both after this
    // effect's initial measure. Without observing the DOM the step-1 spotlight
    // would latch onto a torn-down node and never recover until a resize/scroll.
    //   context: safe because measure() is idempotent — it bails on an unchanged
    //   rect — so the observer cannot thrash. Scoped to the simulator wrapper so
    //   it only wakes on the simulator's own churn.
    //   context: the phone anchor is also genuinely stable now. The device is
    //   pinned on EVERY phone surface (SimulatorEngine.tsx, grep
    //   fixedHeightPx={PHONE_FIXED_HEIGHT_PX}), flag or no flag,
    //   so a growing message list cannot move it. That was not true while the pin
    //   was scoped to the scripted demo — the default path kept the 560-600px
    //   band, which is what AI-931 reported. The reasoning-card anchor (tour step
    //   4) is a separate question: that card grows 2→4 steps with flexGrow, so it
    //   can still re-measure. Unverifiable in jsdom (no layout), so it wants a
    //   browser check at the narrowest supported viewport.
    const observeTarget = observeRef?.current ?? document.body
    const observer = new MutationObserver(measure)
    observer.observe(observeTarget, { childList: true, subtree: true })
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', onScroll, true)
      if (scrollRaf !== null) window.cancelAnimationFrame(scrollRaf)
      observer.disconnect()
    }
  }, [isActive, measure, observeRef])

  // AI-1117: the final step's anchor (playback-nav) sits at the foot of the
  // simulator and can be below the fold — the tooltip card clamps itself into
  // the viewport (ENG-976) but that only repositions the CARD, leaving the
  // spotlight it's pointing at off-screen. Scroll the new step's anchor into
  // view, but only on a step TRANSITION (tracked by index in a ref) — not on
  // every measure() re-run above (which also fires on the user's own
  // scroll/resize), or this would fight them mid-scroll.
  // targetRect is in the deps (not just read) so a late-mounting anchor gets a
  // retry: measure()'s MutationObserver updates targetRect once the anchor
  // appears, which reruns THIS effect even though isActive/currentIndex/
  // anchorId never change across that transition. measure() is idempotent
  // (bails on an unchanged rect), so this can't cause extra re-runs mid-step.
  const prefersReducedMotion = usePrefersReducedMotion()
  const scrolledIndexRef = useRef<number | null>(null)
  useEffect(() => {
    if (!isActive) {
      // Reset so a fresh tour run (e.g. tour 2 starting after tour 1 finishes)
      // scrolls its own first step instead of no-oping on a stale index match.
      scrolledIndexRef.current = null
      return undefined
    }
    // A centered (modal) step ignores its anchor entirely (see anchorRect
    // below) — there's nothing meaningful to scroll to, and doing so would
    // scroll a real anchor behind the full-screen dim for no visible effect.
    if (currentStep?.centered) return undefined
    if (scrolledIndexRef.current === currentIndex) return undefined
    if (!anchorId) return undefined
    const el = document.querySelector<HTMLElement>(`[data-tour="${anchorId}"]`)
    // jsdom (this repo's test env) does not implement scrollIntoView at all —
    // guard here (mirrors usePrefersReducedMotion's matchMedia guard) rather
    // than requiring every suite that mounts WizardTour to stub it.
    if (!el || typeof el.scrollIntoView !== 'function') return undefined
    // Only commit the "scrolled" mark once the anchor is actually found — a
    // late-mounting anchor (not yet in the DOM this render) must retry on a
    // future render of this effect rather than being permanently skipped.
    scrolledIndexRef.current = currentIndex
    // 'nearest' (not 'center'): the ONLY stated problem is a below-the-fold
    // anchor (playback-nav) needing to be scrolled INTO view — 'nearest' is a
    // no-op once the element is already visible, so paging through
    // already-visible steps doesn't trigger a visible re-scroll. 'center'
    // would also push a tall anchor (e.g. TOUR_ANCHORS.simulator, the whole
    // phone) so its top goes above the fold, which is wrong for a step meant
    // to show the whole simulator. Only `behavior` (smooth vs instant) should
    // differ on reduced motion, not `block`.
    el.scrollIntoView({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'nearest',
    })
    return undefined
  }, [
    isActive,
    currentIndex,
    currentStep?.centered,
    anchorId,
    prefersReducedMotion,
    targetRect,
  ])

  // Arrow-key navigation while the tour is active. The card's focus trap only
  // governs Tab within the card, and on the one interactive step (tour 2's
  // chat input) the spotlit control stays reachable — guard against hijacking
  // a text cursor there.
  useEffect(() => {
    if (!isActive) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return
      if (isEditableTarget(event.target)) return
      if (event.key === 'ArrowRight') next()
      else back()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isActive, next, back])

  if (!isActive || !currentStep) return null

  // Per-step CTA: a step may override the button label (e.g. "Okay, show me",
  // "Got it"); otherwise fall back to Next (mid-tour) / Done (last step). The
  // pager still decides finish-vs-advance from isLast, so a custom mid-tour label
  // advances rather than ends.
  const isLastStep = currentIndex === steps.length - 1
  const cta = currentStep.ctaKey
    ? t(currentStep.ctaKey)
    : isLastStep
    ? t('pager.done')
    : t('pager.next')
  const labels: TourPagerLabels = {
    dotLabel: (step, total) => t('pager.dot', { number: step, total }),
    cta,
  }

  // A modal step (ENG-974) ignores its anchor entirely: no cut-out, a full-screen
  // dim, and the card centered on the viewport — the standard overlay pattern.
  const anchorRect = currentStep.centered ? null : targetRect
  // Same modal-step exemption for the click-blocker's DOM lookup — a centered
  // step has no anchor to block clicks against.
  const blockedAnchorId = currentStep.centered ? null : currentStep.anchorId

  return (
    <>
      <TourSpotlight
        targetRect={anchorRect}
        dimWholeViewport={currentStep.centered}
        interactive={currentStep.interactive}
        anchorId={blockedAnchorId}
      />
      <TourTooltipCard
        anchorRect={anchorRect}
        placement={currentStep.placement}
        title={t(currentStep.titleKey)}
        body={t(currentStep.bodyKey)}
        dialogLabel={t('dialog_label')}
        onDismiss={skip}
      >
        <TourPager
          stepIds={stepIds}
          currentIndex={currentIndex}
          labels={labels}
          onGoTo={goTo}
          onNext={next}
          onFinish={finish}
        />
      </TourTooltipCard>
    </>
  )
}

export default WizardTour
